import { Text, Grid, Box, VStack } from "@chakra-ui/react";
import { Decimal } from "@syla/shared/decimal";
import { satisfiesEntityType } from "@syla/shared/types/helpers/permissions";
import {
  AccountSubType,
  AccountType,
} from "@syla/shared/types/models/AccountBase";
import React, { Fragment } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { Plan } from "../../types/plans/plan";
import { PlanAccount } from "./PlanAccount";

export function ValueProps({
  recommendedPlan,
  currentPlan,
  account,
}: {
  recommendedPlan: Plan;
  currentPlan: Plan | undefined;
  account: PlanAccount;
}) {
  const valueProps = getValueProps({
    recommendedPlan,
    currentPlan,
    account: account,
  });

  return (
    <Grid
      templateColumns={"min-content auto"}
      alignItems={"flex-start"}
      gap={3}
    >
      {valueProps.map(({ text, subText }) => (
        <Fragment key={text}>
          <Box mt="0.2rem">
            <Text color="primary.400">
              <FaCheckCircle />
            </Text>
          </Box>
          <VStack alignItems={"flex-start"} spacing={0.5}>
            <Text fontWeight={"bold"}>{text}</Text>
            {subText && <Text>{subText}</Text>}
          </VStack>
        </Fragment>
      ))}
    </Grid>
  );
}

type ValueProp = {
  text: string;
  subText?: string;
};

/**
 * Determine which value propositions should be displayed on the upsell component
 */
function getValueProps({
  recommendedPlan,
  currentPlan,
  account,
}: {
  recommendedPlan: Plan;
  currentPlan: Plan | undefined;
  account: PlanAccount;
}): ValueProp[] {
  const transactionCap = Decimal.from(
    recommendedPlan.features.transactionLimit.toString(),
  ).format({
    separateThousands: true,
  }).text;

  const valueProps: ValueProp[] = [];

  if (
    !currentPlan ||
    recommendedPlan.features.transactionLimit >
      currentPlan.features.transactionLimit
  ) {
    valueProps.push({
      text: `Up to ${transactionCap} transactions`,
    });
  }

  // crypto tax report
  if (!currentPlan || !satisfiesEntityType({ plan: currentPlan, account })) {
    valueProps.push({
      text:
        account.accountType === AccountType.Individual ?
          `Crypto tax report`
        : `${account.accountType} crypto tax report`,
      subText: `Everything you need to complete your ${account.accountType} tax return.`,
    });
  }

  if (
    !currentPlan?.features.assuranceReports &&
    recommendedPlan.features.assuranceReports
  ) {
    valueProps.push({
      text: "Assurance reports",
      subText: "Additional reports to keep you safe in an ATO audit or review.",
    });
  }

  if (
    !currentPlan?.features.premiumIntegrations &&
    recommendedPlan.features.premiumIntegrations &&
    account.accountType === AccountType.SMSF
  ) {
    valueProps.push({
      text: "BGL sync",
      subText: "Sync transactions directly to BGL SF360.",
    });
  }

  if (
    !currentPlan?.features.tradingStock &&
    recommendedPlan.features.tradingStock &&
    account.accountSubType === AccountSubType.Trader
  ) {
    valueProps.push({
      text: "Trading stock",
      subText: "Trading stock calculations for traders.",
    });
  }

  // tax saving
  if (
    !currentPlan?.features.taxOptimisation &&
    recommendedPlan.features.taxOptimisation
  ) {
    // all entity types, including Company, can still benefit from the tax
    // saving optimisation
    if (account.accountSubType === AccountSubType.Investor) {
      valueProps.push({
        text: "Tax saving",
        subText: "Apply LTFO to reduce your capital gains.",
      });
    }

    // or always show for trading stock
    else if (account.accountSubType === AccountSubType.Trader) {
      valueProps.push({
        text: "Tax saving",
        subText:
          "Optimise your closing stock valuation to reduce your taxable income.",
      });
    }
  }

  return valueProps;
}
